import { useEffect, useState } from 'react';
import { useForm as useReactForm } from 'react-hook-form';

import { toast } from 'sonner';
import { z } from 'zod';
import Honeybadger from '@honeybadger-io/js';
import { zodResolver } from '@hookform/resolvers/zod';

import { deserializer } from '../../../../../shared/utils/deserializer';
import { SUCCESS_MESSAGES } from '../constants';
import {
  fetchReviewRequestConfiguration as apiFetchSettings,
  updateReviewRequestConfiguration as apiUpdateSettings,
} from '../services/reviewRequestConfigurationService';

const defaultValues = {
  confirmationMessage: '',
  content: '',
  sendEmail: false,
  sendSms: false,
  subject: '',
  title: '',
};

const formSchema = z.object({
  confirmationMessage: z.string().min(1, 'Confirmation message is required.'),
  content: z.string().min(1, 'Content is required.'),
  sendEmail: z.boolean(),
  sendSms: z.boolean(),
  subject: z.string().min(1, 'Subject is required.'),
  title: z.string().min(1, 'Title is required.'),
});

const useForm = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const form = useReactForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);

      try {
        const response = await apiFetchSettings();
        const settings = await deserializer.deserialize(response);
        form.reset(settings);
      } catch (error) {
        // Displaying the error may be confusing to the User. Instead, Honeybadger will
        // be notified so that the error can be investigated.
        Honeybadger.notify(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (formData) => {
    try {
      setErrorMessage('');
      setIsLoading(true);

      await apiUpdateSettings(formData);

      toast.success(SUCCESS_MESSAGES.settings.update);
    } catch (error) {
      setErrorMessage(error.message);
      Honeybadger.notify(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    errorMessage,
    form,
    handleSubmit,
    isLoading,
  };
};

export default useForm;
